const normalizeWhitespace = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

export const normalizeEntityName = (value = '') => normalizeWhitespace(value)
  .toLowerCase()
  .replace(/\([^)]*\)/g, ' ')
  .replace(/[^a-z0-9]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

export const calculateNameEditDistance = (left = '', right = '') => {
  const source = normalizeEntityName(left);
  const target = normalizeEntityName(right);

  if (source === target) {
    return 0;
  }

  if (!source) {
    return target.length;
  }

  if (!target) {
    return source.length;
  }

  const previousRow = Array.from({ length: target.length + 1 }, (_, index) => index);

  for (let sourceIndex = 1; sourceIndex <= source.length; sourceIndex += 1) {
    const currentRow = [sourceIndex];

    for (let targetIndex = 1; targetIndex <= target.length; targetIndex += 1) {
      const substitutionCost = source[sourceIndex - 1] === target[targetIndex - 1] ? 0 : 1;
      currentRow[targetIndex] = Math.min(
        previousRow[targetIndex] + 1,
        currentRow[targetIndex - 1] + 1,
        previousRow[targetIndex - 1] + substitutionCost
      );
    }

    for (let index = 0; index < currentRow.length; index += 1) {
      previousRow[index] = currentRow[index];
    }
  }

  return previousRow[target.length];
};

export const calculateNameSimilarity = (left = '', right = '') => {
  const source = normalizeEntityName(left);
  const target = normalizeEntityName(right);
  const maxLength = Math.max(source.length, target.length);

  if (!maxLength) {
    return 1;
  }

  const distance = calculateNameEditDistance(source, target);
  return (maxLength - distance) / maxLength;
};

export const rankEntityNameMatches = ({
  extractedName = '',
  entities = [],
  getNames = (entity) => [entity?.name],
  minSimilarity = 0.72,
  limit = 3,
} = {}) => {
  const normalizedExtractedName = normalizeEntityName(extractedName);
  if (!normalizedExtractedName) {
    return [];
  }

  return (Array.isArray(entities) ? entities : [])
    .map((entity) => {
      const candidateNames = [...new Set(
        (Array.isArray(getNames(entity)) ? getNames(entity) : [])
          .map(normalizeWhitespace)
          .filter(Boolean)
      )];
      const scoredNames = candidateNames.map((name) => {
        const normalizedName = normalizeEntityName(name);
        const exact = normalizedName === normalizedExtractedName;
        const contains = Boolean(
          normalizedName
          && normalizedExtractedName
          && (normalizedName.includes(normalizedExtractedName) || normalizedExtractedName.includes(normalizedName))
        );
        const similarity = exact || contains
          ? 1
          : calculateNameSimilarity(normalizedExtractedName, normalizedName);

        return {
          name,
          normalizedName,
          similarity,
          exact,
        };
      });
      const best = scoredNames.sort((left, right) => right.similarity - left.similarity)[0] || null;

      return best ? {
        entity,
        matchedName: best.name,
        normalizedMatchedName: best.normalizedName,
        score: best.similarity,
        exact: best.exact,
      } : null;
    })
    .filter(Boolean)
    .filter((match) => match.exact || match.score >= minSimilarity)
    .sort((left, right) => {
      if (left.exact !== right.exact) return left.exact ? -1 : 1;
      return right.score - left.score;
    })
    .slice(0, limit);
};
